// Promise polyfill
import 'core-js/features/promise';

import {render} from 'react-dom';
import {HashRouter} from 'react-router-dom';
import App from './components/App';
import '@amzn/awsui-global-styles/polaris.css';
import {getMidwayJwtToken} from './auth/MidwayJwtToken';
import axios from 'axios';
import {initializeAppSettings} from './config/AppSettings';
import initializeCloudWatchRUM, {recordRumEvent, getNetworkInfo, drainPendingEventsToFallback} from "./config/CloudWatchRum";
import UnauthenticatedPage from "./components/UnauthenticatedPage";

const pageLoadStart = performance.now();

// Add Authorization header to all axios calls
axios.interceptors.request.use(async (config) => {
    const jwtToken = await getMidwayJwtToken();
    config.headers.Authorization = `Bearer ${jwtToken}`
    return config;
});

// Track auth errors (403, invalid token) for observability
axios.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response) {
            const status = error.response.status;
            if (status === 403 || status === 401) {
                recordRumEvent('auth_error', {
                    status,
                    url: error.config?.url || 'unknown',
                    timestamp: new Date().toISOString(),
                    ...getNetworkInfo()
                });
            }
        }
        return Promise.reject(error);
    }
);

(async () => {
    // Track auth timing
    const authStart = performance.now();
    try {
        await getMidwayJwtToken()
    } catch (e) {
      render(<UnauthenticatedPage/>, document.querySelector('#app'))
      recordRumEvent('page_load_failed', {
          reason: 'auth_failure',
          authDurationMs: performance.now() - authStart,
          totalDurationMs: performance.now() - pageLoadStart,
          ...getNetworkInfo()
      });
      drainPendingEventsToFallback();
      return;
    }
    const authDurationMs = performance.now() - authStart;

    // Initialize application settings
    const settingsStart = performance.now();
    const appSettings = (await axios('/settings.json')).data;
    initializeAppSettings(appSettings);
    const settingsDurationMs = performance.now() - settingsStart;

    initializeCloudWatchRUM();

    // Record page bootstrap timing
    const bootstrapDurationMs = performance.now() - pageLoadStart;
    recordRumEvent('page_bootstrap', {
        authDurationMs,
        settingsDurationMs,
        bootstrapDurationMs,
        page: window.location.hash || '#/',
        ...getNetworkInfo()
    });

    // Set up a check for frustrated loads (>15 seconds from page start)
    const elapsedSoFar = performance.now() - pageLoadStart;
    const remainingMs = 15000 - elapsedSoFar;
    const initialHash = window.location.hash || '#/';
    if (remainingMs <= 0) {
        // Bootstrap alone exceeded 15s — already frustrated
        recordRumEvent('frustrated_load', {
            totalDurationMs: elapsedSoFar,
            authDurationMs,
            settingsDurationMs,
            page: initialHash,
            ...getNetworkInfo()
        });
    } else {
        setTimeout(() => {
            const currentHash = window.location.hash || '#/';
            // Skip if user navigated away from the original page
            if (currentHash !== initialHash) return;

            // Only fire if charts haven't loaded yet (page still loading)
            if (document.querySelectorAll('[graph-name]').length === 0) {
                recordRumEvent('frustrated_load', {
                    totalDurationMs: performance.now() - pageLoadStart,
                    authDurationMs,
                    settingsDurationMs,
                    page: currentHash,
                    ...getNetworkInfo()
                });
            }
        }, remainingMs);
    }

    render(
        <HashRouter>
            <App/>
        </HashRouter>,
        document.querySelector('#app')
    );
})();
